

import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Copy, 
  Link, 
  Users, 
  Trash2, 
  Globe, 
  Lock, 
  Mail,
  Check,
  X,
  ExternalLink
} from "lucide-react";
import { api } from "@/lib/api-client";
import { toast } from "sonner";

interface ShareDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  documentId: string;
  documentTitle: string;
}

interface Share {
  id: string;
  shareType: string;
  isPublic: boolean;
  canEdit: boolean;
  canShare: boolean;
  canDownload: boolean;
  token: string;
  expiresAt: string | null;
  sharedWithUser?: {
    id: string;
    name: string | null;
    email: string;
    image: string | null;
  };
}

const EXPIRY_OPTIONS = [
  { value: "never", label: "Nunca" },
  { value: "3600", label: "1 hora" },
  { value: "86400", label: "1 día" },
  { value: "604800", label: "1 semana" },
  { value: "2592000", label: "1 mes" },
];

export function ShareDialog({ open, onOpenChange, documentId, documentTitle }: ShareDialogProps) {
  const [shares, setShares] = useState<Share[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("link");
  
  // Link share options
  const [isPublic, setIsPublic] = useState(false);
  const [shareType, setShareType] = useState("view");
  const [expiresIn, setExpiresIn] = useState("never");
  const [canEdit, setCanEdit] = useState(false);
  const [canDownload, setCanDownload] = useState(false);
  const [password, setPassword] = useState("");
  const [shareUrl, setShareUrl] = useState("");
  
  // User share options
  const [userEmail, setUserEmail] = useState("");
  const [userShareType, setUserShareType] = useState("view");
  const [userCanEdit, setUserCanEdit] = useState(false);
  const [userCanShare, setUserCanShare] = useState(false);

  useEffect(() => {
    if (open && documentId) {
      loadShares();
    }
  }, [open, documentId]);

  const loadShares = async () => {
    setError(null);
    try {
      const data = await api.get<{ success?: boolean; shares?: Share[]; share?: { token: string }; error?: string }>(`/share?documentId=${documentId}`);
      
      if (data.success) {
        setShares(data.shares || []);
        // Set link share if exists
        const linkShare = data.shares?.find((s: Share) => s.isPublic);
        if (linkShare) {
          setIsPublic(true);
          setShareType(linkShare.shareType);
          setCanEdit(linkShare.canEdit);
          setCanDownload(linkShare.canDownload);
          setShareUrl(`${window.location.origin}/shared/${linkShare.token}`);
        } else {
          setIsPublic(false);
          setShareUrl("");
        }
      } else {
        console.error("Error loading shares:", data.error);
        setError(data.error || "Error al cargar comparticiones");
        toast.error(data.error || "Error al cargar comparticiones");
      }
    } catch (error) {
      console.error("Error loading shares:", error);
      setError("Error de conexión");
      toast.error("Error al cargar comparticiones");
    }
  };

  const createLinkShare = async () => {
    setLoading(true);
    try {
      const data = await api.post<{ success?: boolean; share?: { token: string }; error?: string }>('/share', {
        documentId,
        isPublic: true,
        shareType,
        canEdit,
        canDownload,
        password: password || undefined,
        expiresIn: expiresIn !== "never" ? parseInt(expiresIn) : undefined,
      });
      console.log("Create share response:", data);
      if (data.success) {
        const url = `${window.location.origin}/shared/${data.share?.token}`;
        setShareUrl(url);
        toast.success("Enlace de compartir creado");
        loadShares();
      } else {
        toast.error(data.error || "Error al crear enlace");
      }
    } catch (error) {
      toast.error("Error al crear enlace");
    } finally {
      setLoading(false);
    }
  };

  const shareWithUser = async () => {
    if (!userEmail.trim()) {
      toast.error("Ingresa un correo electrónico");
      return;
    }

    setLoading(true);
    try {
      const data = await api.post<{ success?: boolean; error?: string }>('/share', {
        documentId,
        sharedWithUserEmail: userEmail,
        shareType: userShareType,
        canEdit: userCanEdit,
        canShare: userCanShare,
      });
      if (data.success) {
        toast.success(`Compartido con ${userEmail}`);
        setUserEmail("");
        loadShares();
      } else {
        toast.error(data.error || "Error al compartir");
      }
    } catch (error) {
      toast.error("Error al compartir");
    } finally {
      setLoading(false);
    }
  };

  const deleteShare = async (shareId: string) => {
    try {
      const data = await api.delete<{ success?: boolean }>(`/share?id=${shareId}`);
      if (data.success) {
        toast.success("Compartido eliminado");
        loadShares();
        // Reset state if it was the public link
        const wasPublic = shares.find(s => s.id === shareId && s.isPublic);
        if (wasPublic) {
          setIsPublic(false);
          setShareUrl("");
        }
      }
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const copyToClipboard = async (text: string) => {
    if (navigator.clipboard) {
      await navigator.clipboard.writeText(text);
    } else {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
    }
    toast.success("Copiado al portapapeles");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Compartir "{documentTitle}"</DialogTitle>
          <DialogDescription>
            Comparte este documento con un enlace público o con usuarios específicos
          </DialogDescription>
        </DialogHeader>

        {error && (
          <div className="bg-destructive/10 text-destructive p-3 rounded-md text-sm">
            {error}
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="link" className="flex items-center gap-2">
              <Link className="w-4 h-4" />
              Enlace
            </TabsTrigger>
            <TabsTrigger value="users" className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Usuarios
            </TabsTrigger>
          </TabsList>

          {/* Link Tab */}
          <TabsContent value="link" className="space-y-4">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2">
                  {isPublic ? <Globe className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                  Enlace público
                </Label>
                <Checkbox 
                  checked={isPublic} 
                  onCheckedChange={async (checked) => {
                    const newValue = checked as boolean;
                    
                    if (newValue) {
                      // Create public link when checked
                      setLoading(true);
                      try {
                        const data = await api.post<{ success?: boolean; shares?: Share[]; share?: { token: string }; error?: string }>('/share', {
                          documentId,
                          isPublic: true,
                          shareType: "view",
                          canEdit: false,
                          canDownload: false,
                        });
                        console.log("Create share on check:", data);
                        
                        if (data.success) {
                          setIsPublic(true);
                          const url = `${window.location.origin}/shared/${data.share?.token}`;
                          setShareUrl(url);
                          toast.success("Enlace de compartir creado");
                          loadShares();
                        } else {
                          toast.error(data.error || "Error al crear enlace");
                        }
                      } catch (error) {
                        console.error("Error creating share:", error);
                        toast.error("Error al crear enlace");
                      } finally {
                        setLoading(false);
                      }
                    } else {
                      // Delete public link when unchecked
                      const publicShare = shares.find(s => s.isPublic);
                      if (publicShare) deleteShare(publicShare.id);
                      setIsPublic(false);
                      setShareUrl("");
                    }
                  }} 
                />
              </div>

              {isPublic && (
                <>
                  <div className="space-y-2">
                    <Label>Permisos</Label>
                    <Select value={shareType} onValueChange={setShareType}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="view">Solo lectura</SelectItem>
                        <SelectItem value="edit">Puede editar</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Expira en</Label>
                    <Select value={expiresIn} onValueChange={setExpiresIn}>
                      <SelectTrigger>
                        <SelectValue placeholder="Nunca" />
                      </SelectTrigger>
                      <SelectContent>
                        {EXPIRY_OPTIONS.map(option => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center gap-2">
                    <Checkbox 
                      id="canEdit" 
                      checked={canEdit} 
                      onCheckedChange={(c) => setCanEdit(c as boolean)} 
                    />
                    <Label htmlFor="canEdit" className="text-sm">Puede editar</Label>
                  </div>

                  <div className="flex items-center gap-2">
                    <Checkbox 
                      id="canDownload" 
                      checked={canDownload} 
                      onCheckedChange={(c) => setCanDownload(c as boolean)} 
                    />
                    <Label htmlFor="canDownload" className="text-sm">Puede descargar</Label>
                  </div>

                  <div className="space-y-2">
                    <Label>Contraseña (opcional)</Label>
                    <Input 
                      type="password" 
                      placeholder="Proteger con contraseña"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                  </div>

                  <Button onClick={createLinkShare} disabled={loading} className="w-full">
                    {loading ? "Creando..." : "Crear enlace"}
                  </Button>

                  {shareUrl && (
                    <div className="flex gap-2 mt-4">
                      <Input value={shareUrl} readOnly className="flex-1" />
                      <Button variant="outline" size="icon" onClick={() => copyToClipboard(shareUrl)}>
                        <Copy className="w-4 h-4" />
                      </Button>
                      <Button variant="outline" size="icon" asChild>
                        <a href={shareUrl} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="space-y-4">
            <div className="space-y-3">
              <div className="flex gap-2">
                <div className="flex-1">
                  <Input 
                    placeholder="Correo electrónico"
                    type="email"
                    value={userEmail}
                    onChange={(e) => setUserEmail(e.target.value)}
                  />
                </div>
                <Select value={userShareType} onValueChange={setUserShareType}>
                  <SelectTrigger className="w-28">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="view">Ver</SelectItem>
                    <SelectItem value="edit">Editar</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex gap-2">
                <div className="flex items-center gap-2">
                  <Checkbox 
                    id="userCanEdit" 
                    checked={userCanEdit} 
                    onCheckedChange={(c) => setUserCanEdit(c as boolean)} 
                  />
                  <Label htmlFor="userCanEdit" className="text-sm">Editar</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox 
                    id="userCanShare" 
                    checked={userCanShare} 
                    onCheckedChange={(c) => setUserCanShare(c as boolean)} 
                  />
                  <Label htmlFor="userCanShare" className="text-sm">Compartir</Label>
                </div>
              </div>

              <Button onClick={shareWithUser} disabled={loading} className="w-full">
                <Mail className="w-4 h-4 mr-2" />
                {loading ? "Compartiendo..." : "Compartir"}
              </Button>
            </div>

            {/* Shared with users list */}
            {shares.filter(s => !s.isPublic).length > 0 && (
              <div className="space-y-2 pt-4 border-t">
                <Label className="text-sm text-muted-foreground">Compartido con</Label>
                {shares.filter(s => !s.isPublic).map((share) => (
                  <div 
                    key={share.id} 
                    className="flex items-center justify-between p-2 rounded-md bg-muted"
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm">
                        {share.sharedWithUser?.name?.[0]?.toUpperCase() || share.sharedWithUser?.email?.[0]?.toUpperCase() || "U"}
                      </div>
                      <div>
                        <p className="text-sm font-medium">
                          {share.sharedWithUser?.name || share.sharedWithUser?.email}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {share.shareType === "view" ? "Solo lectura" : share.shareType === "edit" ? "Puede editar" : "Administrador"}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {share.canEdit && (
                        <Badge variant="outline" className="text-xs">Editar</Badge>
                      )}
                      {share.canShare && (
                        <Badge variant="outline" className="text-xs">Compartir</Badge>
                      )}
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-8 w-8 text-destructive"
                        onClick={() => deleteShare(share.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
